
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useOrganizationContext } from '@/context/organization-provider';
import { useThemeUtils } from '@/hooks/useThemeUtils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Palette, Settings, Save, Users } from 'lucide-react';
import LogoUpload from '@/components/ui/logo-upload';
import { MembershipSettings } from './MembershipSettings';
import PaymentSettings from './PaymentSettings';

interface OrganizationSettingsProps {
  organizationId?: string;
}

export const OrganizationSettings = ({ organizationId: explicitOrgId }: OrganizationSettingsProps = {}) => {
  const { organization } = useOrganizationContext();
  
  // Use explicit org ID if provided (super admin context), otherwise use context org
  const targetOrgId = explicitOrgId || organization?.id;
  
  const { 
    settings,
    getSetting, 
    orgSettingsLoading: loading, 
    setOrganizationTheme: updateSetting 
  } = useThemeUtils({ organizationId: targetOrgId });
  
  const [orgData, setOrgData] = useState({
    display_name: '',
    logo_url: '',
  });
  
  const [themeConfig, setThemeConfig] = useState({
    // Core brand colors for organization white-labeling
    primary_color: '#3B82F6',
    secondary_color: '#1E40AF',
    accent_color: '#10B981',
    warning_color: '#F59E0B',
    error_color: '#EF4444',
    
    // Layout preferences
    border_radius: 'medium', // none, small, medium, large, full
    font_scale: 'normal', // compact, normal, comfortable
  });
  
  const [featureSettings, setFeatureSettings] = useState({
    enable_quotes: true,
    enable_tracking: true,
    require_insurance: false,
    enable_notifications: true,
  });
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (organization) {
      setOrgData({
        display_name: organization.display_name || '',
        logo_url: organization.logo_url || '',
      });
    }
  }, [organization]);

  useEffect(() => {
    if (!loading) {
      setThemeConfig({
        // Core brand colors
        primary_color: getSetting('theme_primary_color') || '#3B82F6',
        secondary_color: getSetting('theme_secondary_color') || '#1E40AF',
        accent_color: getSetting('theme_accent_color') || '#10B981',
        warning_color: getSetting('theme_warning_color') || '#F59E0B',
        error_color: getSetting('theme_error_color') || '#EF4444',
        
        // Layout preferences
        border_radius: getSetting('theme_border_radius') || 'medium',
        font_scale: getSetting('theme_font_scale') || 'normal',
      });
      
      setFeatureSettings({
        enable_quotes: getSetting('feature_enable_quotes') === 'true',
        enable_tracking: getSetting('feature_enable_tracking') === 'true',
        require_insurance: getSetting('feature_require_insurance') === 'true',
        enable_notifications: getSetting('feature_enable_notifications') === 'true',
      });
    }
  }, [loading, settings]);

  const handleLogoChange = (url: string | null) => {
    setOrgData(prev => ({ ...prev, logo_url: url || '' }));
  };

  const handleSaveOrganization = async () => {
    if (!organization) return;
    
    try {
      setSaving(true);
      const { error } = await supabase
        .from('organizations')
        .update({
          display_name: orgData.display_name,
          logo_url: orgData.logo_url,
        })
        .eq('id', organization.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Organization details updated successfully",
      });
    } catch (error) {
      console.error('Error updating organization:', error);
      toast({
        title: "Error",
        description: "Failed to update organization details",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTheme = async () => {
    try {
      setSaving(true);
      
      // Save all settings - React Query will invalidate and trigger theme re-application
      await Promise.all([
        // Core brand colors
        updateSetting('theme_primary_color', themeConfig.primary_color),
        updateSetting('theme_secondary_color', themeConfig.secondary_color),
        updateSetting('theme_accent_color', themeConfig.accent_color),
        updateSetting('theme_warning_color', themeConfig.warning_color),
        updateSetting('theme_error_color', themeConfig.error_color),
        
        // Layout preferences
        updateSetting('theme_border_radius', themeConfig.border_radius),
        updateSetting('theme_font_scale', themeConfig.font_scale),
      ]);

      toast({
        title: "Success",
        description: "Theme settings saved successfully. Changes will apply automatically.",
      });
    } catch (error) {
      console.error('Error saving theme:', error);
      toast({
        title: "Error",
        description: "Failed to save theme settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFeatures = async () => {
    try {
      setSaving(true);
      await Promise.all([
        updateSetting('feature_enable_quotes', featureSettings.enable_quotes.toString()),
        updateSetting('feature_enable_tracking', featureSettings.enable_tracking.toString()),
        updateSetting('feature_require_insurance', featureSettings.require_insurance.toString()),
        updateSetting('feature_enable_notifications', featureSettings.enable_notifications.toString()),
      ]);

      toast({
        title: "Success",
        description: "Feature settings saved successfully",
      });
    } catch (error) {
      console.error('Error saving features:', error);
      toast({
        title: "Error",
        description: "Failed to save feature settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Membership Settings */}
      <MembershipSettings organizationId={targetOrgId} />
      
      {/* Payment Settings */}
      <PaymentSettings />
      
      {/* Organization Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Details</span>
          </CardTitle>
          <CardDescription>
            Manage your organization's basic information and branding
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name</Label>
              <Input 
                id="org-name" 
                value={organization?.name || ''} 
                readOnly 
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Contact support to change organization name
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="display-name">Display Name</Label>
              <Input 
                id="display-name" 
                value={orgData.display_name} 
                onChange={(e) => setOrgData({ ...orgData, display_name: e.target.value })}
                placeholder="Friendly display name" 
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subdomain">Subdomain</Label>
            <Input 
              id="subdomain" 
              value={organization?.subdomain || ''} 
              readOnly 
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Your organization URL: {organization?.subdomain}.parcelpilot.app
            </p>
          </div>

          <LogoUpload
            onLogoChange={handleLogoChange}
            currentLogo={orgData.logo_url}
            organizationName={orgData.display_name || organization?.name || 'Organization'}
            disabled={saving}
          />

          <Button onClick={handleSaveOrganization} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Organization Details'}
          </Button>
        </CardContent>
      </Card>

      {/* Theme Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Palette className="h-5 w-5" />
            <span>Theme Configuration</span>
          </CardTitle>
          <CardDescription>
            Customize your organization's brand colors for white-label theming
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs defaultValue="brand" className="w-full">
            <div className="border-b border-border">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="brand">Brand Colors</TabsTrigger>
                <TabsTrigger value="layout">Layout</TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="brand" className="space-y-4">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                 <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex items-center space-x-2">
                     <Input 
                       type="color" 
                       value={themeConfig.primary_color}
                       onChange={(e) => setThemeConfig({ ...themeConfig, primary_color: e.target.value })}
                       className="w-20 h-10"
                       disabled={saving}
                     />
                     <Input 
                       value={themeConfig.primary_color}
                       onChange={(e) => setThemeConfig({ ...themeConfig, primary_color: e.target.value })}
                       className="flex-1 font-mono"
                       disabled={saving}
                     />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Secondary Color</Label>
                  <div className="flex items-center space-x-2">
                     <Input 
                       type="color" 
                       value={themeConfig.secondary_color}
                       onChange={(e) => setThemeConfig({ ...themeConfig, secondary_color: e.target.value })}
                       className="w-20 h-10"
                       disabled={saving}
                     />
                     <Input 
                       value={themeConfig.secondary_color}
                       onChange={(e) => setThemeConfig({ ...themeConfig, secondary_color: e.target.value })}
                       className="flex-1 font-mono"
                       disabled={saving}
                     />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Accent Color</Label>
                  <div className="flex items-center space-x-2">
                     <Input 
                       type="color" 
                       value={themeConfig.accent_color}
                       onChange={(e) => setThemeConfig({ ...themeConfig, accent_color: e.target.value })}
                       className="w-20 h-10"
                       disabled={saving}
                     />
                     <Input 
                       value={themeConfig.accent_color}
                       onChange={(e) => setThemeConfig({ ...themeConfig, accent_color: e.target.value })}
                       className="flex-1 font-mono"
                       disabled={saving}
                     />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Warning Color</Label>
                  <div className="flex items-center space-x-2">
                     <Input 
                       type="color" 
                       value={themeConfig.warning_color}
                       onChange={(e) => setThemeConfig({ ...themeConfig, warning_color: e.target.value })}
                       className="w-20 h-10"
                       disabled={saving}
                     />
                     <Input 
                       value={themeConfig.warning_color}
                       onChange={(e) => setThemeConfig({ ...themeConfig, warning_color: e.target.value })}
                       className="flex-1 font-mono"
                       disabled={saving}
                     />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Error Color</Label>
                  <div className="flex items-center space-x-2">
                     <Input 
                       type="color" 
                       value={themeConfig.error_color}
                       onChange={(e) => setThemeConfig({ ...themeConfig, error_color: e.target.value })}
                       className="w-20 h-10"
                       disabled={saving}
                     />
                     <Input 
                       value={themeConfig.error_color}
                       onChange={(e) => setThemeConfig({ ...themeConfig, error_color: e.target.value })}
                       className="flex-1 font-mono"
                       disabled={saving}
                     />
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="layout" className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Border Radius</Label>
                  <select 
                    value={themeConfig.border_radius}
                    onChange={(e) => setThemeConfig({ ...themeConfig, border_radius: e.target.value })}
                    className="w-full p-2 border rounded"
                    disabled={saving}
                  >
                    <option value="none">None (Square)</option>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                    <option value="full">Full (Rounded)</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label>Font Scale</Label>
                  <select 
                    value={themeConfig.font_scale}
                    onChange={(e) => setThemeConfig({ ...themeConfig, font_scale: e.target.value })}
                    className="w-full p-2 border rounded"
                    disabled={saving}
                  >
                    <option value="compact">Compact</option>
                    <option value="normal">Normal</option>
                    <option value="comfortable">Comfortable</option>
                  </select>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Simple Brand Color Preview */}
          <div className="space-y-4 p-4 bg-muted/10 rounded-lg border">
            <h4 className="text-sm font-medium">Brand Color Preview</h4>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-8 h-8 rounded border border-border"
                  style={{ backgroundColor: themeConfig.primary_color }}
                />
                <span className="text-sm">Primary</span>
              </div>
              <div className="flex items-center space-x-2">
                <div 
                  className="w-8 h-8 rounded border border-border"
                  style={{ backgroundColor: themeConfig.secondary_color }}
                />
                <span className="text-sm">Secondary</span>
              </div>
              <div className="flex items-center space-x-2">
                <div 
                  className="w-8 h-8 rounded border border-border"
                  style={{ backgroundColor: themeConfig.accent_color }}
                />
                <span className="text-sm">Accent</span>
              </div>
              <div className="flex items-center space-x-2">
                <div 
                  className="w-8 h-8 rounded border border-border"
                  style={{ backgroundColor: themeConfig.warning_color }}
                />
                <span className="text-sm">Warning</span>
              </div>
              <div className="flex items-center space-x-2">
                <div 
                  className="w-8 h-8 rounded border border-border"
                  style={{ backgroundColor: themeConfig.error_color }}
                />
                <span className="text-sm">Error</span>
              </div>
            </div>
            
            {/* Sample buttons with brand colors */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <button 
                className="p-2 rounded text-white text-sm font-medium transition-opacity hover:opacity-80"
                style={{ backgroundColor: themeConfig.primary_color }}
              >
                Primary
              </button>
              <button 
                className="p-2 rounded text-white text-sm font-medium transition-opacity hover:opacity-80"
                style={{ backgroundColor: themeConfig.secondary_color }}
              >
                Secondary
              </button>
              <button 
                className="p-2 rounded text-white text-sm font-medium transition-opacity hover:opacity-80"
                style={{ backgroundColor: themeConfig.accent_color }}
              >
                Accent
              </button>
              <button 
                className="p-2 rounded text-white text-sm font-medium transition-opacity hover:opacity-80"
                style={{ backgroundColor: themeConfig.warning_color }}
              >
                Warning
              </button>
              <button 
                className="p-2 rounded text-white text-sm font-medium transition-opacity hover:opacity-80"
                style={{ backgroundColor: themeConfig.error_color }}
              >
                Error
              </button>
            </div>
          </div>

          <Button onClick={handleSaveTheme} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Theme'}
          </Button>
        </CardContent>
      </Card>

      {/* Feature Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Feature Settings</CardTitle>
          <CardDescription>
            Configure organization-specific features and preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Quote System</Label>
                <p className="text-sm text-muted-foreground">
                  Allow customers to request and receive quotes
                </p>
              </div>
              <Switch
                checked={featureSettings.enable_quotes}
                onCheckedChange={(checked) => setFeatureSettings({ ...featureSettings, enable_quotes: checked })}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Customer Tracking</Label>
                <p className="text-sm text-muted-foreground">
                  Allow customers to track their parcel deliveries
                </p>
              </div>
              <Switch
                checked={featureSettings.enable_tracking}
                onCheckedChange={(checked) => setFeatureSettings({ ...featureSettings, enable_tracking: checked })}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Require Insurance</Label>
                <p className="text-sm text-muted-foreground">
                  Make insurance mandatory for all bookings
                </p>
              </div>
              <Switch
                checked={featureSettings.require_insurance}
                onCheckedChange={(checked) => setFeatureSettings({ ...featureSettings, require_insurance: checked })}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Send email and SMS notifications to customers
                </p>
              </div>
              <Switch
                checked={featureSettings.enable_notifications}
                onCheckedChange={(checked) => setFeatureSettings({ ...featureSettings, enable_notifications: checked })}
              />
            </div>
          </div>

          <Button onClick={handleSaveFeatures} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Feature Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
